"use client";

/**
 * useViewerRegistrations
 *
 * Loads the logged-in viewer's confirmed activity registrations for the current event and exposes
 * them as fast-lookup sets, so schedule renderers can mark each schedule item as registered.
 *
 * Identity is resolved server-side inside `listViewerRegisteredKeys` (auth-derived — no
 * caller-controlled identity params). The lookup re-runs whenever the viewer's identity appears
 * (sign-in mid-page, hydration, account switch) or `registrationsVersion` changes (a signup/cancel
 * made anywhere on the page), keeping the "Registered" pills in sync.
 *
 * Returns `null` when there is no logged-in attendee (anonymous public visitor or builder mode).
 */

import { useEffect, useRef, useState } from "react";

import { listViewerRegisteredKeys, type ViewerRegisteredKeys } from "../_logic/actions/public-actions";
import { usePublicViewContext } from "../_components/public/PublicViewContext";
import type { ViewerRegistrationKeys } from "../_logic/utils/schedule-registration-match";

/** Projects the server's array-shaped registration keys into the Set-based lookup the renderers use. */
function toRegistrationSets(keys: ViewerRegisteredKeys): ViewerRegistrationKeys {
  return {
    slotIds: new Set(keys.slotIds),
    activityIds: new Set(keys.activityIds),
    waitlistedSlotIds: new Set(keys.waitlistedSlotIds),
    waitlistedActivityIds: new Set(keys.waitlistedActivityIds),
  };
}

/**
 * Viewer's registrations as status-bucketed lookup sets, or `null` when no
 * attendee identity is present. Shape matches the canonical matcher's
 * {@link ViewerRegistrationKeys} so renderers can pass it straight through.
 */
export type ViewerRegistrations = ViewerRegistrationKeys;

/**
 * Loads and keeps the current viewer's confirmed registrations in sync.
 *
 * @returns The viewer's registrations, or `null` when there's no logged-in attendee.
 */
export function useViewerRegistrations(): ViewerRegistrations | null {
  const { isPublic, eventId, attendeeId, viewer, registrationsVersion, initialViewerRegisteredKeys } =
    usePublicViewContext();
  // Seed from the server prefetch so the "Registered"/"Waitlisted" pills are correct on first paint.
  const [registrations, setRegistrations] = useState<ViewerRegistrations | null>(() =>
    initialViewerRegisteredKeys ? toRegistrationSets(initialViewerRegisteredKeys) : null
  );
  // Skip the mount fetch when seeded (state already reflects load time); later identity changes /
  // registrationsVersion bumps still re-fetch.
  const skipMountFetchRef = useRef(initialViewerRegisteredKeys != null);

  useEffect(() => {
    if (!isPublic) return;
    if (attendeeId == null && !viewer?.email) {
      setRegistrations(null);
      return;
    }
    if (skipMountFetchRef.current) {
      skipMountFetchRef.current = false;
      return;
    }
    let cancelled = false;
    listViewerRegisteredKeys(eventId)
      .then((keys) => {
        if (cancelled) return;
        setRegistrations(toRegistrationSets(keys));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("useViewerRegistrations: failed to load viewer registrations", { eventId }, error);
        setRegistrations(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isPublic, eventId, attendeeId, viewer?.email, registrationsVersion]);

  return registrations;
}
